<template>
    <div class="tile position-relative">
        <div class="tile-title">
            <h2>Websites</h2>
        </div>
        <v-repeater
            :provider="provider"
            :mapRows="repeaterRows"
            :pagination="true"
            ref="websiteList"
            table-class="fs-block table-definition">
            <template #iteratee="{item}">
                <website-item
                    @open-update-modal="openUpdateModal"
                    @open-delete-modal="openDeleteModal"
                    :key="item.id"
                    :website="item"
                    :websites-custom-fields="websitesCustomFields"
                />
            </template>
            <template slot="no-content">
                <v-no-content>
                    <span slot="title">You don't have any websites</span>
                    <span slot="actions">
                        <router-link
                            v-if="$auth.$pages['add-website'].isAllowed"
                            :to="{name: 'add-website', params: {organizationId: organization.id}}"
                            class="btn btn-link">Add Website<v-icon class="margin-left-xs" icon="arrow-right"/></router-link>
                    </span>
                </v-no-content>
            </template>
        </v-repeater>
        <!-- Edit Website Modal -->
        <v-modal v-if="websiteUpdate" title="Edit Website" @close="closeUpdateModal" size="small">
            <div slot="content">
                <v-error-messages
                    :has-frontend-errors="$v.websiteUpdate.$error"
                    class="stack-l">
                    <li class="list-item" v-if="!$v.websiteUpdate.name.required">Website name is required</li>
                    <li class="list-item" v-if="!$v.websiteUpdate.url.required">Domain is required</li>
                    <li class="list-item" v-if="!$v.websiteUpdate.url.url">Domain url is invalid.</li>
                    <li class="list-item" v-if="!$v.websiteUpdate.servicePhone.required">Customer service phone is required</li>
                    <li class="list-item" v-if="!$v.websiteUpdate.serviceEmail.required">Customer service email is invalid</li>
                    <li class="list-item" v-if="!$v.websiteUpdate.serviceEmail.email">Customer service email is invalid</li>
                </v-error-messages>
                <p class="stack-m">Edit your pricing plan information.</p>
                <div class="grid-content">
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Website Name"
                            :validate="$v.websiteUpdate.name"
                            v-model="websiteUpdate.name"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Website ID"
                            v-model="websiteUpdate.id"
                            disabled/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Domain"
                            :validate="$v.websiteUpdate.url"
                            v-model="websiteUpdate.url"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Customer Service Phone"
                            :validate="$v.websiteUpdate.servicePhone"
                            v-model="websiteUpdate.servicePhone"/>
                    </div>
                    <div class="grid-item grid-item-full">
                        <r-input
                            label="Customer Service Email"
                            :validate="$v.websiteUpdate.serviceEmail"
                            v-model="websiteUpdate.serviceEmail"/>
                    </div>
                    <v-custom-fields-manager
                        fluid
                        resource-type="websites"
                        :preload-custom-fields="websitesCustomFields"
                        v-model="websiteUpdate.customFields"/>
                </div>
            </div>
            <template slot="actions">
                <r-button type="primary" @click="updateWebsite">Update Website</r-button>
            </template>
        </v-modal>
        <!-- Delete Website Modal -->
        <v-modal v-if="websiteDelete" title="Delete Website" @close="closeDeleteModal" size="small">
            <div slot="content">
                <p v-if="websiteDelete.inUse">This website cannot be deleted because it has already been used.</p>
                <p v-else>Are you sure you want to delete this website?</p>
            </div>
            <template slot="actions" v-if="!websiteDelete.inUse">
                <r-button type="danger" @click="deleteWebsite">Delete Website</r-button>
            </template>
        </v-modal>
    </div>
</template>

<script>
    import OrganizationModel from '@/common/models/organization-model';
    import WebsiteModel from '@/common/models/website-model';
    import {
        vErrorMessages,
        vIcon,
    } from '@/components/form';
    import {required, url, email} from 'vuelidate/lib/validators';
    import vModal from '@/components/modal/v-modal';
    import vNoContent from '@/components/v-no-content';
    import vRepeater from '@/components/repeater/repeater';
    import vCustomFieldsManager from '@/components/custom-fields/v-custom-fields-manager';
    import WebsiteItem from './website-item';

    export default {
        name: 'website-list',
        components: {
            vModal,
            vIcon,
            vNoContent,
            vErrorMessages,
            vCustomFieldsManager,
            vRepeater,
            WebsiteItem,
        },
        validations: {
            websiteUpdate: {
                name: {required},
                url: {required, url},
                servicePhone: {required},
                serviceEmail: {required, email},
            },
        },
        props: {
            organization: {
                type: OrganizationModel,
            },
            websitesCustomFields: Array,
        },
        data() {
            return {
                websiteUpdate: null,
                websiteDelete: null,
            };
        },
        computed: {
            customFields() {
                return this.getCustomFields('website', this.getCustomFieldsTypes(this.websitesCustomFields));
            },
        },
        methods: {
            async updateWebsite() {
                this.$v.$touch();

                if (this.$v.websiteUpdate.$error) {
                    return;
                }

                try {
                    await this.$api(({websites}) => websites.update({id: this.websiteUpdate.id, data: this.websiteUpdate}));
                    await this.$refs.websiteList.fetchData.call();
                    this.$toast.positive(`Website successfully updated`);
                } catch (error) {
                    this.$toast.fromError(error, `Website data could not be updated, please contact support`);
                } finally {
                    this.closeUpdateModal();
                }
            },
            async deleteWebsite() {
                try {
                    await this.$api(({websites}) => websites.delete({id: this.websiteDelete.id}));
                    await this.$refs.websiteList.fetchData.call();
                    this.$toast.positive(`Website successfully deleted`);
                } catch (error) {
                    this.$toast.fromError(error, `Website data could not be deleted, please contact support`);
                } finally {
                    this.closeDeleteModal();
                }
            },
            openUpdateModal(website) {
                this.$v.$reset();
                this.websiteUpdate = new WebsiteModel(website);
            },
            async openDeleteModal(website) {
                this.$v.$reset();

                try {
                    const {items} = await this.$api(({transactions}) => transactions.getAll({filter: `websiteId:${website.id}`, limit: 1}));
                    const inUse = items.length > 0;
                    this.websiteDelete = new WebsiteModel({...website, inUse});
                } catch (error) {
                    throw error;
                }
            },
            closeUpdateModal() {
                this.websiteUpdate = null;
                this.$v.$reset();
            },
            closeDeleteModal() {
                this.websiteDelete = null;
                this.$v.$reset();
            },
            async provider(params = {}) {
                params.filter = `organizationId:${this.organization.id}`;
                params.sort = `-createdTime`;
                return this.$api(({websites}) => websites.getAll(params));
            },
            repeaterRows(websiteItems) {
                return websiteItems.map(website => new WebsiteModel(website.fields));
            },
        },
    };
</script>
